import React from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge, Button, StatusBadge } from '@/components/ui';
import { getRoleBadgeVariant, getRoleIcon } from '@/utils/role';
import type { AdminUser } from '../types';

interface AdminUsersDesktopTableProps {
  users: AdminUser[];
  onToggleLock: (id: string, isActive: boolean) => void;
  onViewDetail: (id: string) => void;
  onEdit: (user: AdminUser) => void;
  onImpersonate: (user: AdminUser) => void;
}

export function AdminUsersDesktopTable({ users, onToggleLock, onViewDetail, onEdit, onImpersonate }: AdminUsersDesktopTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nhân sự</TableHead>
          <TableHead>Tổ chức</TableHead>
          <TableHead>Vai trò</TableHead>
          <TableHead>Trạng thái</TableHead>
          <TableHead className="text-right">Hành động</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          const isPersonal = !user.company_id || !user.company_name || user.company_name.toLowerCase().includes('cá nhân');
          return (
          <TableRow
            key={user.id}
            className="group hover:bg-slate-50/80 transition-all border-slate-50 cursor-pointer"
            onClick={() => onViewDetail(user.id)}
          >
            <TableCell className="py-4 pl-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 font-medium group-hover:bg-brand-50 group-hover:text-brand-600 transition-all duration-300">
                  {user.avatar_path ? (
                    <img src={user.avatar_path} alt="" className="w-full h-full rounded-xl object-cover" />
                  ) : (
                    user.full_name.charAt(0)
                  )}
                </div>
                <div>
                  <p className="font-medium text-os-text leading-none mb-1 group-hover:text-brand-700 sm:text-sm text-xs">{user.full_name}</p>
                  <p className="text-xs text-os-muted font-medium">{user.email}</p>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all ${isPersonal ? 'bg-slate-50 border-slate-100 group-hover:bg-slate-100' : 'bg-brand-50/30 border-brand-100 group-hover:bg-white group-hover:border-brand-200 group-hover:shadow-sm'}`}>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold border ${isPersonal ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-brand-600 border-brand-100 shadow-sm'}`}>
                  {user.company_code ? user.company_code.charAt(0) : '👤'}
                </div>
                <div>
                  <p className={`text-xs font-bold leading-none ${isPersonal ? 'text-slate-500' : 'text-brand-700'}`}>
                    {user.company_name || 'Cá nhân'}
                  </p>
                  <p className={`text-xs font-medium tracking-tight mt-0.5 ${isPersonal ? 'text-slate-400' : 'text-brand-600/70'}`}>
                    {user.company_code || 'GUEST'}
                  </p>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant={getRoleBadgeVariant(user.role_code)} className="font-medium text-xs px-2 py-0.5 flex items-center gap-1 w-fit">
                <i className={getRoleIcon(user.role_code)} />
                {user.role_name || user.role_code}
              </Badge>
            </TableCell>
            <TableCell>
              {user.is_active ? (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="text-[11px] sm:text-xs font-medium text-emerald-700">Hoạt động</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200">
                  <span className="relative flex h-2 w-2">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-400"></span>
                  </span>
                  <span className="text-[11px] sm:text-xs font-medium text-slate-600">Đã khóa</span>
                </div>
              )}
            </TableCell>
            <TableCell className="pr-6">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-brand-600 hover:bg-brand-50"
                  onClick={(e) => { e.stopPropagation(); onViewDetail(user.id); }}
                >
                  <i className="ri-eye-line" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={user.is_active ? "text-slate-400 hover:text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleLock(user.id, !user.is_active);
                  }}
                >
                  <i className={user.is_active ? "ri-lock-2-line" : "ri-lock-unlock-line"} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                  onClick={(e) => { e.stopPropagation(); onEdit(user); }}
                >
                  <i className="ri-edit-line" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                  title="Nhập vai"
                  onClick={(e) => { e.stopPropagation(); onImpersonate(user); }}
                >
                  <i className="ri-user-shared-line" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
